import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, MessageSquare } from "lucide-react";
import { listPostsByTag } from "@/lib/forum-extra.functions";

export const Route = createFileRoute("/forum/tagg/$slug")({
  loader: async ({ params }) => {
    const res = await listPostsByTag({ data: { slug: params.slug } });
    if (!res.tag) throw notFound();
    return res;
  },
  head: ({ loaderData, params }) => {
    const name = loaderData?.tag?.name ?? params.slug;
    const title = `#${name} – trådar i Fritidsforum`;
    const desc = `Alla trådar taggade ${name} i Fritidsforum – erfarenheter, frågor och svar om husbilar och husvagnar från svenska ägare.`;
    const url = `https://www.akhusbilfritid.se/forum/tagg/${params.slug}`;
    return {
      meta: [
        { title: title.slice(0, 60) },
        { name: "description", content: desc.slice(0, 158) },
        { property: "og:title", content: title },
        { property: "og:description", content: desc.slice(0, 158) },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  notFoundComponent: () => (
    <section className="bg-warm">
      <div className="container-edge py-24 text-center">
        <h1 className="font-display text-4xl">Taggen finns inte</h1>
        <Link to="/forum" className="mt-6 inline-flex items-center gap-2 text-accent hover:underline">
          <ArrowLeft size={15} /> Till forumet
        </Link>
      </div>
    </section>
  ),
  component: TagPage,
});

function TagPage() {
  const { tag, posts: rawPosts } = Route.useLoaderData();
  const posts = (rawPosts ?? []) as Array<{
    id: string;
    slug: string;
    title: string;
    body: string;
    comment_count: number;
    created_at: string;
    author: { display_name: string | null };
  }>;

  return (
    <section className="bg-warm">
      <div className="container-edge py-10 md:py-16">
        <Link to="/forum" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={15} /> Fritidsforum
        </Link>

        <h1 className="mt-6 font-display text-4xl leading-tight md:text-5xl">#{tag?.name}</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
          {posts.length} tråd{posts.length === 1 ? "" : "ar"} taggade med {tag?.name}. Ren erfarenhet från
          ägare — inte broschyrtext.
        </p>

        <ul className="mt-8 space-y-3">
          {posts.map((p) => (
            <li key={p.id} className="rounded-2xl border border-border bg-card p-5">
              <Link
                to="/forum/t/$slug"
                params={{ slug: p.slug }}
                className="font-display text-xl leading-snug hover:text-accent"
              >
                {p.title}
              </Link>
              <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.body}</p>
              <p className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{p.author.display_name || "Medlem"}</span>
                <span className="inline-flex items-center gap-1">
                  <MessageSquare size={12} /> {p.comment_count}
                </span>
                <span>{new Date(p.created_at).toLocaleDateString("sv-SE")}</span>
              </p>
            </li>
          ))}
          {!posts.length && (
            <li className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Inga trådar med den här taggen ännu — bli först.{" "}
              <Link to="/forum/nytt" className="font-semibold text-accent hover:underline">
                Skriv ett inlägg
              </Link>
            </li>
          )}
        </ul>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/forum/nytt"
            className="inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
          >
            Nytt inlägg
          </Link>
        </div>
      </div>
    </section>
  );
}
